import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { X, Tag, User, AlertCircle, Loader2, Briefcase, Radio } from 'lucide-react';
import { fetchWithAuth, makeAbsolutePhotoUrl, condoApi } from '../../../services/api';
import toast from 'react-hot-toast';
import Select, { SingleValue, MultiValue } from 'react-select';
import { cn } from '../../../utils/cn';
import { TagRadixSelector } from '../../TagRadixSelector';
import { TagRadix, applyTagRadix, isAmbiguousTagValue } from '../../../utils/tagValueRadix';
import { UnitSelector, UnitOption } from '../../UnitSelector';
import { CondoType, getUnitLabel } from '../../../utils/terminology';

interface Person {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  profile_picture_url?: string;
}

export interface PersonTag {
  id: string;
  person_id: string;
  customer_id: string;
  tag_value: string;
  tag_value_dec?: number;
  label?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  person?: Person;
  tag_type?: string;
  target_device_ids?: string[];
  target_devices?: { id: string; name: string }[];
}

interface TagFormData {
  tag_value: string;
  label?: string;
  notes?: string;
}

type SelectOption = SingleValue<{ value: string; label: string }>;
type MultiSelectOption = MultiValue<{ value: string; label: string }>;

const TAG_TYPE_OPTIONS = [
  { value: 'passiva', label: 'Tag Passiva' },
  { value: 'ativa', label: 'Tag Ativa' },
  { value: 'controle_remoto', label: 'Controle Remoto' },
  { value: 'outro', label: 'Outro' },
];

export interface TagFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customerId: string;
  customerType: 'condominio' | 'empresa';
  editingTag?: PersonTag | null;
  preselectedPerson?: Person | null;
  preselectedOwnerType?: string;
  condoType?: CondoType;
  darkMode?: boolean;
}

const PersonAvatar: React.FC<{ person: Person; size?: 'sm' | 'md' }> = ({ person, size = 'md' }) => {
  const photoUrl = makeAbsolutePhotoUrl(person.profile_picture_url);
  const dim = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={`${person.first_name} ${person.last_name}`}
        className={cn(dim, 'rounded-lg object-cover flex-shrink-0')}
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <div className={cn(dim, 'rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0')}>
      <User className={cn(size === 'sm' ? 'h-4 w-4' : 'h-4 w-4', 'text-white')} />
    </div>
  );
};

export const TagFormModal: React.FC<TagFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  customerId,
  customerType,
  editingTag,
  preselectedPerson,
  condoType,
  darkMode = true,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Person[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showPersonSelector, setShowPersonSelector] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const personInputRef = useRef<HTMLInputElement>(null);

  // Unit / Department filtering. The unit side uses the shared UnitSelector so a street
  // condo narrows by Rua + Número the same way the vehicle form does — a bare Quadra list
  // makes every lot with the same number indistinguishable.
  const [departments, setDepartments] = useState<any[]>([]);
  const [selectedUnitOption, setSelectedUnitOption] = useState<UnitOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption>(null);

  // Tag type + target device narrowing (Guarita receptors)
  const [guaritaDevices, setGuaritaDevices] = useState<{ id: string; name: string }[]>([]);
  const [selectedTagType, setSelectedTagType] = useState<SelectOption>(null);
  const [selectedDevices, setSelectedDevices] = useState<MultiSelectOption>([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<TagFormData>({ mode: 'onBlur' });

  // DEC/HEX answer for an all-digit tag value (see utils/tagValueRadix).
  const watchedTagValue = watch('tag_value') || '';
  const [tagRadix, setTagRadix] = useState<TagRadix | null>(null);
  const radixValueRef = useRef<string | null>(null);

  // The answer belongs to one specific number, not to the form — typing a different
  // value must drop it, or "hex" answered for one card silently pads the next one.
  // Editing keeps the meaning the stored value already has today (decimal, which is
  // how every consumer reads an unpadded all-digit string), so opening an old tag to
  // fix its label can never change which card it points at.
  useEffect(() => {
    const trimmed = watchedTagValue.trim();
    if (radixValueRef.current === trimmed) return;
    radixValueRef.current = trimmed;
    const isStoredValue = !!editingTag && trimmed === (editingTag.tag_value || '').trim();
    setTagRadix(isStoredValue && isAmbiguousTagValue(trimmed) ? 'dec' : null);
  }, [watchedTagValue, editingTag]);

  const fetchDepartments = useCallback(async () => {
    if (!customerId || customerType !== 'empresa') return;
    try {
      const res = await condoApi.getDepartments(customerId);
      setDepartments(res.data?.data || res.data || []);
    } catch { /* ignore */ }
  }, [customerId, customerType]);

  const fetchGuaritaDevices = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await condoApi.getCustomerIoTDevices(customerId);
      const all = res.data?.devices || [];
      setGuaritaDevices(all.filter((d: any) => d.device_type === 'guarita').map((d: any) => ({ id: d.id, name: d.name })));
    } catch { /* ignore */ }
  }, [customerId]);

  useEffect(() => {
    if (open) {
      if (customerType === 'empresa') fetchDepartments();
      fetchGuaritaDevices();
    }
  }, [open, customerType, fetchDepartments, fetchGuaritaDevices]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (editingTag) {
        reset({ tag_value: editingTag.tag_value, label: editingTag.label || '', notes: editingTag.notes || '' });
        setSelectedPerson(editingTag.person || preselectedPerson || null);
        setSelectedTagType(TAG_TYPE_OPTIONS.find(o => o.value === editingTag.tag_type) || null);
        setSelectedDevices((editingTag.target_devices || []).map(d => ({ value: d.id, label: d.name })));
      } else {
        reset({ tag_value: '', label: '', notes: '' });
        setSelectedPerson(preselectedPerson || null);
        setSelectedTagType(null);
        setSelectedDevices([]);
      }
      setSearchTerm('');
      setSearchResults([]);
      setShowPersonSelector(false);
      setSubmitAttempted(false);
      setSelectedUnitOption(null);
      setSelectedDepartment(null);
    }
  }, [open, editingTag, reset, preselectedPerson]);

  // Search for people — filtered by unit/dept if selected, or by search term
  useEffect(() => {
    const hasSearchTerm = searchTerm.trim().length > 0;
    const hasUnitFilter = !!selectedUnitOption;
    const hasDeptFilter = !!selectedDepartment;

    if (!hasSearchTerm && !hasUnitFilter && !hasDeptFilter) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const params = new URLSearchParams({
      include_inactive_roles: 'false',
      page: '1',
      page_size: '50',
    });
    if (hasSearchTerm) params.append('search', searchTerm.trim());
    if (selectedUnitOption) params.append('unit_id', selectedUnitOption.value);
    if (selectedDepartment) params.append('department_id', selectedDepartment.value);

    const debounce = setTimeout(async () => {
      try {
        const response = await fetchWithAuth(`/api/v1/customers/${customerId}/people?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          const results = data.data || data.people || [];
          setSearchResults(results);
          if ((hasUnitFilter || hasDeptFilter) && results.length > 0) {
            setShowPersonSelector(true);
          }
        }
      } catch (err) {
        console.error('Error searching people:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, customerId, selectedUnitOption, selectedDepartment, open]);

  const onSubmit = async (data: TagFormData) => {
    setSubmitAttempted(true);

    if (!editingTag && !selectedPerson) {
      toast.error('Por favor, selecione a pessoa');
      personInputRef.current?.focus();
      return;
    }

    const rawTagValue = data.tag_value.trim();
    if (isAmbiguousTagValue(rawTagValue) && !tagRadix) {
      toast.error('Informe se o valor da tag é decimal ou hexadecimal');
      return;
    }

    setIsSubmitting(true);
    try {
      const url = editingTag
        ? `/api/v1/customers/${customerId}/person-tags/${editingTag.id}`
        : `/api/v1/customers/${customerId}/person-tags`;

      const body: Record<string, any> = {
        // A value the operator marked as hex is stored padded with a leading zero —
        // the form every reader and every consumer here already treats as hex.
        tag_value: applyTagRadix(rawTagValue, tagRadix),
        label: data.label?.trim() || undefined,
        notes: data.notes?.trim() || undefined,
        tag_type: selectedTagType?.value || undefined,
        // Always sent as an array (even empty) — omitting it on update would leave
        // the previous target_device_ids untouched instead of clearing it.
        target_device_ids: selectedDevices.map(d => d.value),
      };
      if (!editingTag && selectedPerson) {
        body.person_id = selectedPerson.id;
      }

      const response = await fetchWithAuth(url, {
        method: editingTag ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success(editingTag ? 'Tag atualizada!' : 'Tag cadastrada!');
        onSuccess();
        onClose();
      } else {
        const err = await response.json();
        toast.error(err.error || err.message || 'Erro ao salvar tag');
      }
    } catch (err) {
      console.error('Error saving tag:', err);
      toast.error('Erro ao salvar tag');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  // Custom styles for react-select (dark/light theme)
  const selectStyles = darkMode ? {
    control: (base: any) => ({
      ...base,
      backgroundColor: '#111827',
      borderColor: '#1f2937',
      minHeight: '36px',
      '&:hover': { borderColor: '#374151' },
    }),
    menu: (base: any) => ({ ...base, backgroundColor: '#111827', border: '1px solid #1f2937' }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? '#1f2937' : '#111827',
      color: '#e5e7eb',
    }),
    singleValue: (base: any) => ({ ...base, color: '#e5e7eb' }),
    input: (base: any) => ({ ...base, color: '#e5e7eb' }),
    placeholder: (base: any) => ({ ...base, color: '#6b7280' }),
    clearIndicator: (base: any) => ({ ...base, color: '#6b7280', '&:hover': { color: '#e5e7eb' } }),
  } : {
    control: (base: any) => ({
      ...base,
      backgroundColor: '#ffffff',
      borderColor: '#e5e7eb',
      minHeight: '36px',
      '&:hover': { borderColor: '#9ca3af' },
    }),
    menu: (base: any) => ({ ...base, backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? '#f3f4f6' : '#ffffff',
      color: '#111827',
    }),
    singleValue: (base: any) => ({ ...base, color: '#111827' }),
    input: (base: any) => ({ ...base, color: '#111827' }),
    placeholder: (base: any) => ({ ...base, color: '#9ca3af' }),
    clearIndicator: (base: any) => ({ ...base, color: '#9ca3af', '&:hover': { color: '#111827' } }),
  };

  // Theme-aware class helpers
  const bg = darkMode ? 'bg-gray-950' : 'bg-white';
  const bgInput = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const bgInputSubtle = darkMode ? 'bg-gray-900/50' : 'bg-gray-50';
  const bgDropdown = darkMode ? 'bg-gray-900' : 'bg-white';
  const bgHover = darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100';
  const border = darkMode ? 'border-gray-800' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-gray-200' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textFaint = darkMode ? 'text-gray-500' : 'text-gray-400';
  const placeholder = darkMode ? 'placeholder-gray-500' : 'placeholder-gray-400';

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
      <div className={cn(bg, 'border rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto', border)}>
        {/* Header */}
        <div className={cn(bg, 'px-6 py-4 border-b flex items-center justify-between sticky top-0 z-10', border)}>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <Tag className="h-5 w-5 text-white" />
            </div>
            <h3 className={cn('text-lg font-semibold', textPrimary)}>
              {editingTag ? 'Editar Tag de Acesso' : 'Nova Tag de Acesso'}
            </h3>
          </div>
          <button onClick={onClose} disabled={isSubmitting} className={cn('transition-colors', textMuted, darkMode ? 'hover:text-gray-200' : 'hover:text-gray-700')}>
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4">
          <div className="space-y-4">

            {/* Tag Value */}
            <div>
              <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Valor da Tag *</label>
              <input
                {...register('tag_value', {
                  required: 'Valor da tag é obrigatório',
                  pattern: {
                    value: /^[a-zA-Z0-9,xX]{3,50}$/,
                    message: 'Use DEC (12345), HEX (2A30F0) ou Wiegand (042,12528)',
                  },
                })}
                type="text"
                placeholder="Ex: 12345678, 2A30F0, 042,12528"
                disabled={isSubmitting}
                className={cn(
                  'w-full px-3 py-2 text-sm rounded-lg border focus:outline-none',
                  bgInput, textPrimary, placeholder,
                  errors.tag_value ? 'border-red-500' : cn(border, 'focus:border-orange-500')
                )}
              />
              {errors.tag_value && (
                <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />{errors.tag_value.message}
                </p>
              )}
              <p className={cn('mt-1 text-xs', textFaint)}>Aceita DEC (12345678), HEX (2A30F0) ou Wiegand (042,12528)</p>
              <TagRadixSelector
                name="tag-value-radix"
                value={watchedTagValue}
                radix={tagRadix}
                onChange={setTagRadix}
                showError={submitAttempted}
                disabled={isSubmitting}
                darkMode={darkMode}
              />
            </div>

            {/* Tag type */}
            <div>
              <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Tipo de Tag</label>
              <Select
                options={TAG_TYPE_OPTIONS}
                value={selectedTagType}
                onChange={(opt) => setSelectedTagType(opt)}
                placeholder="Selecione o tipo..."
                isClearable
                isDisabled={isSubmitting}
                styles={selectStyles}
                className="text-sm"
              />
            </div>

            {/* Target devices — narrow to specific Guarita receptor(s) */}
            {guaritaDevices.length > 0 && (
              <div>
                <label className={cn('block text-sm font-medium mb-1', textSecondary)}>
                  <Radio className="h-3.5 w-3.5 inline mr-1" />Dispositivos / Receptores
                </label>
                <Select
                  isMulti
                  options={guaritaDevices.map(d => ({ value: d.id, label: d.name }))}
                  value={selectedDevices}
                  onChange={(opts) => setSelectedDevices(opts)}
                  placeholder="Todos os receptores autorizados"
                  isDisabled={isSubmitting}
                  styles={selectStyles}
                  className="text-sm"
                />
                <p className={cn('mt-1 text-xs', textFaint)}>
                  Deixe em branco para liberar em todos os receptores que a pessoa já tem acesso. Selecione para restringir a receptores específicos.
                </p>
              </div>
            )}

            {/* Label */}
            <div>
              <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Rótulo (Opcional)</label>
              <input
                {...register('label')}
                type="text"
                placeholder="Ex: Cartão Principal, Chaveiro..."
                disabled={isSubmitting}
                className={cn('w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:border-orange-500', border, bgInput, textPrimary, placeholder)}
              />
            </div>

            {/* Person section — only for new tags */}
            {!editingTag && (
              <>
                {/* Preselected person — read-only */}
                {preselectedPerson ? (
                  <div>
                    <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Pessoa</label>
                    <div className={cn('flex items-center gap-3 p-3 rounded-lg border', border, bgInputSubtle)}>
                      <PersonAvatar person={preselectedPerson} />
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-sm font-medium', textPrimary)}>
                          {preselectedPerson.first_name} {preselectedPerson.last_name}
                        </p>
                        {preselectedPerson.email && (
                          <p className={cn('text-xs truncate', textMuted)}>{preselectedPerson.email}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Unit filter (condominio) — Rua/Número/Quadra/Lote on street condos */}
                    {customerType === 'condominio' && (
                      <div>
                        <label className={cn('block text-sm font-medium mb-1', textSecondary)}>
                          {getUnitLabel(condoType)} (Opcional)
                        </label>
                        <UnitSelector
                          customerId={customerId}
                          value={selectedUnitOption}
                          onChange={(opt) => { setSelectedUnitOption(opt); setSelectedPerson(null); }}
                          condoType={condoType}
                          darkMode={darkMode}
                          disabled={isSubmitting}
                        />
                      </div>
                    )}

                    {/* Department filter (empresa) */}
                    {customerType === 'empresa' && (
                      <div>
                        <label className={cn('block text-sm font-medium mb-1', textSecondary)}>
                          <Briefcase className="h-3.5 w-3.5 inline mr-1" />Departamento
                        </label>
                        <Select
                          options={departments.map(d => ({ value: d.id, label: d.name }))}
                          value={selectedDepartment}
                          onChange={(opt) => { setSelectedDepartment(opt); setSelectedPerson(null); }}
                          placeholder="Todos os departamentos"
                          isClearable
                          isDisabled={isSubmitting}
                          styles={selectStyles}
                          className="text-sm"
                        />
                      </div>
                    )}

                    {/* Person search */}
                    <div>
                      <label className={cn('block text-sm font-medium mb-1', textSecondary)}>
                        Pessoa *
                        {!selectedPerson && submitAttempted && (
                          <span className="text-red-400 text-xs ml-1">(obrigatório)</span>
                        )}
                      </label>

                      {selectedPerson ? (
                        <div className={cn('flex items-center gap-3 p-3 rounded-lg border', border, bgInput)}>
                          <PersonAvatar person={selectedPerson} />
                          <div className="flex-1 min-w-0">
                            <p className={cn('text-sm font-medium', textPrimary)}>
                              {selectedPerson.first_name} {selectedPerson.last_name}
                            </p>
                            {selectedPerson.email && (
                              <p className={cn('text-xs truncate', textMuted)}>{selectedPerson.email}</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => { setSelectedPerson(null); setSearchTerm(''); }}
                            disabled={isSubmitting}
                            className={cn('transition-colors hover:text-red-400', textMuted)}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="relative">
                          <input
                            ref={personInputRef}
                            type="text"
                            placeholder="Buscar pessoa..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setShowPersonSelector(true)}
                            disabled={isSubmitting}
                            className={cn(
                              'w-full px-3 py-2 text-sm rounded-lg border focus:outline-none',
                              bgInput, textPrimary, placeholder,
                              !selectedPerson && submitAttempted
                                ? 'border-red-500'
                                : cn(border, 'focus:border-orange-500')
                            )}
                          />
                          {isSearching && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <Loader2 className="h-4 w-4 text-orange-500 animate-spin" />
                            </div>
                          )}

                          {/* Search results dropdown */}
                          {showPersonSelector && searchResults.length > 0 && (
                            <div className={cn('absolute top-full mt-1 w-full rounded-lg shadow-2xl max-h-56 overflow-y-auto z-20 border', bgDropdown, border)}>
                              {searchResults.map((person) => (
                                <button
                                  key={person.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedPerson(person);
                                    setShowPersonSelector(false);
                                    setSearchTerm('');
                                  }}
                                  className={cn('w-full flex items-center gap-3 p-3 text-left transition-colors', bgHover)}
                                >
                                  <PersonAvatar person={person} size="sm" />
                                  <div className="flex-1 min-w-0">
                                    <p className={cn('text-sm font-medium', textPrimary)}>
                                      {person.first_name} {person.last_name}
                                    </p>
                                    {person.email && (
                                      <p className={cn('text-xs truncate', textMuted)}>{person.email}</p>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          {showPersonSelector && (searchTerm.trim() || selectedUnitOption || selectedDepartment) && !isSearching && searchResults.length === 0 && (
                            <div className={cn('absolute top-full mt-1 w-full rounded-lg shadow-2xl p-3 z-20 border', bgDropdown, border)}>
                              <p className={cn('text-sm text-center', textMuted)}>Nenhum resultado encontrado</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Notes */}
            <div>
              <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Observações</label>
              <textarea
                {...register('notes')}
                rows={3}
                placeholder="Observações sobre esta tag..."
                disabled={isSubmitting}
                className={cn('w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:border-orange-500 resize-none', border, bgInput, textPrimary, placeholder)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className={cn('mt-6 flex justify-end gap-3 pt-4 border-t', border)}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className={cn('px-4 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50', border, bgInput, bgHover, textSecondary)}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingTag ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>

      {showPersonSelector && (
        <div className="fixed inset-0 z-10" onClick={() => setShowPersonSelector(false)} />
      )}
    </div>
  );
};
